// In-page anchor scrolling is driven here, not by the browser. Chromium's
// native smooth scroll (html { scroll-behavior: smooth }) computes its
// destination once and undershoots on long jumps, which used to force a
// visible second corrective scroll. Instead the CSS keeps instant behavior
// and same-page anchor clicks run a single rAF animation that re-reads the
// target's position every frame, so it always lands exactly where the
// element's scroll-margin says — one motion, no follow-up nudge.
const target = () => {
  const id = decodeURIComponent(window.location.hash.slice(1))
  return id ? document.getElementById(id) : null
}

const destination = (el) => {
  const margin = parseFloat(getComputedStyle(el).scrollMarginTop) || 0
  const max = document.documentElement.scrollHeight - window.innerHeight
  return Math.min(max, Math.max(0, window.scrollY + el.getBoundingClientRect().top - margin))
}

const aligned = (el) => Math.abs(window.scrollY - destination(el)) <= 2

let frame = null
const cancelScroll = () => {
  if (frame === null) return
  cancelAnimationFrame(frame)
  frame = null
}
// A wheel spin or touch drag mid-animation means the user took over.
for (const event of ["wheel", "touchstart"]) {
  window.addEventListener(event, cancelScroll, { passive: true })
}

const scrollToTarget = (el) => {
  cancelScroll()
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    window.scrollTo(0, destination(el))
    return
  }
  const from = window.scrollY
  const distance = Math.abs(destination(el) - from)
  const duration = Math.min(800, Math.max(300, distance / 4))
  const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2)
  const started = performance.now()
  const step = (now) => {
    const t = Math.min(1, (now - started) / duration)
    window.scrollTo(0, from + (destination(el) - from) * ease(t))
    frame = t < 1 ? requestAnimationFrame(step) : null
  }
  frame = requestAnimationFrame(step)
}

document.addEventListener("click", (event) => {
  if (event.defaultPrevented || event.button !== 0) return
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
  const link = event.target.closest("a[href*='#']")
  if (!link) return
  const url = new URL(link.href, window.location.href)
  if (url.origin !== window.location.origin) return
  if (url.pathname !== window.location.pathname || url.search !== window.location.search) return
  const el = document.getElementById(decodeURIComponent(url.hash.slice(1)))
  if (!el) return
  event.preventDefault()
  if (url.hash !== window.location.hash) history.pushState(null, "", url.hash)
  scrollToTarget(el)
})

// Cross-page fragment visits still go through Turbo Drive, which scrolls
// while the incoming page is settling and never re-anchors; back/forward
// hash navigation stays native too. Both scrolls are instant now, so after
// they finish one instant correction is imperceptible. scrollend is missing
// in Safari and never fires when no scroll was needed, so a timeout
// backstops it; whichever comes first runs the single correction.
const correct = () => {
  const el = target()
  if (el && !aligned(el)) window.scrollTo(0, destination(el))
}

const afterScroll = () => {
  if (!target()) return
  let done = false
  const finish = () => {
    if (done) return
    done = true
    window.removeEventListener("scrollend", finish)
    correct()
  }
  window.addEventListener("scrollend", finish, { once: true })
  setTimeout(finish, 1600)
}

document.addEventListener("turbo:load", afterScroll)
window.addEventListener("hashchange", afterScroll)
